<template>
  <b-container fluid>
    <group-componentized-module
      v-if="!!isSensorComponentized"
      :options="sensorInformation"
      :selected-information="selectedInformation"
      @on-change-selected-information="onChangeSelectedInformationHandler"
    />
    <general-and-parameters-layout
      :loading="loading"
      :variables="variables"
      :group-list="groupListHandler"
      :assetModuleId="selectedAssetModuleId"
      @create-group="createGroup"
      @apply-filter="applyFilter"
      @quit-renaming="quitRenaming"
      @get-group-grid="getGroupList"
      @reset-variable="resetVariable"
      @rename-group-request="renameGroupRequest"
      @delete-group-request="deleteGroupRequest"
      @moved-variable-by-drag="changeVariableGroup"
      @toggle-variable-visibility="toggleVisibility"
      @rename-variable-request="renameVariableRequest"
      @change-variable-group-by-button="changeVariableGroup"
      @restoreGroups="restoreGroups"
      @search-change="searchChange"
    />
  </b-container>
</template>
<script>
import eventBus from '@/services/globalEventBus';
import event from '@/domain/events/engineeringModulesHeader';
import ParametersTabService from '@/domain/services/installation/supervision/sensors/generalAndParameters';
import GeneralAndParametersLayout from '@/domain/components/installation/supervision/sensors/GeneralAndParametersLayout';
import GroupComponentizedModule from '@/domain/components/installation/supervision/sensors/GroupComponentizedModule';
import { alertModes } from '@/services/messages/alertService';
import { ModuleCategorys } from 'sigma-mappings';

export default {
  name: 'GeneralTab',
  props: {
    isSensorComponentized: {
      type: Boolean,
      default: false,
    },
    category: String,
    selectedInformation: {
      type: Object,
      default: () => ({}),
    },
    sensorInformation: {
      type: Array,
      default: () => ([]),
    },
  },
  components: {
    GroupComponentizedModule,
    GeneralAndParametersLayout,
  },
  data: () => ({
    loading: false,
    showAll: false,
    groupList: [],
    variables: [],
    filter: [],
    selectedVariables: [],
  }),
  computed: {
    selectedAssetModuleId() {
      const { sensorId } = this.$route.params;
      const { assetModuleId } = this.selectedInformation || {};
      return this.category === ModuleCategorys.COMPONENTIZED
        ? assetModuleId
        : sensorId;
    },
    variableList() {
      return this.groupList
        .map(({ variables }) => variables)
        .reduce((item, index) => item.concat(index), []);
    },
    groupListHandler() {
      const filteredVariables = this.groupList.map(item => ({
        ...item,
        variables: item.variables.filter(({ id }) => this.filter.includes(id)),
      }));
      const filteredGroups = filteredVariables.filter(({ variables }) => variables.length);
      return this.filter.length ? filteredGroups : this.groupList;
    },
    groupWithoutReading() {
      return this.groupList.map(({ name, id }) => ({
        name,
        id,
      }));
    },
    dropdownItems() {
      return [
        {
          text: this.$t('actions.edit-group'),
          iconClass: 'fas fa-pencil-alt mr-2',
        },
        {
          text: this.$t('actions.delete-group'),
          iconClass: 'far fa-trash-alt mr-2',
          textColor: 'text-danger',
        },
      ];
    },
  },
  created() {
    this.getGroupList();
    eventBus.on(event.refresh, this.refreshData, this);
  },
  beforeDestroy() {
    eventBus.off(event.refresh, this.refreshData, this);
  },
  watch: {
    groupList() {
      this.variables = this.variableList;
    },
  },
  methods: {
    onChangeSelectedInformationHandler(value) {
      this.$emit('on-change-selected-information', value);
    },
    showAllHandler(value) {
      this.showAll = !value;
    },
    refreshData({ wait }) {
      wait(this.getGroupList());
    },
    getGroupList() {
      this.loading = true;
      return ParametersTabService
        .getParametersTabList(this.selectedAssetModuleId)
        .then((data) => {
          this.groupList = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    quitRenaming() {
      this.groupList = this.groupList.filter(({ id }) => id);
    },
    createGroup() {
      if (!this.groupList.length) {
        return;
      }
      const { type } = this.groupList[0];
      this.groupList = this.groupList.concat({
        type,
        name: null,
        id: null,
        variables: [],
      });
    },
    apiCreateGroup(name, type) {
      const payload = {
        type,
        name,
        assetModuleId: this.selectedAssetModuleId,
      };
      const lastGroupIndex = this.groupList.length - 1;
      ParametersTabService
        .createNewSupervisionGroup(payload)
        .then(({ id }) => {
          this.groupList[lastGroupIndex].id = id;
          this.$pushAlert({ code: 'MG006', mode: alertModes.TOAST });
        })
        .catch(() => {
          this.quitRenaming();
        });
    },
    renameGroupRequest({ newName, id, type }) {
      if (!id) {
        return this.apiCreateGroup(newName, type);
      }
      return ParametersTabService
        .renameGroup({ newName }, id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        })
        .catch(() => {
          this.getGroupList();
        });
    },
    deleteGroupRequest(id) {
      ParametersTabService
        .deleteGroup(id)
        .then(() => {
          this.getGroupList();
          this.$pushAlert({ code: 'MG007', mode: alertModes.TOAST });
        });
    },
    renameVariableRequest({ name, id }) {
      const groupIndex = this.groupList.findIndex(({ variables }) => {
        const ids = variables.map(item => item.id);
        return ids.includes(id);
      });
      const variableIndex = this.groupList[groupIndex].variables.findIndex(item => item.id === id);
      ParametersTabService
        .renameVariable({ name }, id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
          this.groupList[groupIndex].variables[variableIndex].name = name;
          this.groupList[groupIndex].variables[variableIndex].hasOriginalName = false;
        });
    },
    applyFilter(filter) {
      this.filter = filter.map(({ id }) => id);
    },
    resetVariable(id) {
      ParametersTabService
        .resetVariableName(id)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
          this.getGroupList();
        });
    },
    toggleVisibility({ id, visibility }) {
      ParametersTabService
        .toggleVariableVisibility(id, visibility)
        .then(() => {
          this.getGroupList();
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        });
    },
    changeVariableGroup(payload) {
      const {
        destinyGroupId,
        originGroupId,
        variableId,
        groupSupervisionFieldId,
        order,
      } = payload;
      ParametersTabService
        .moveVariable({
          destinyGroupId,
          originGroupId,
          groupSupervisionFieldId,
          order,
        }, variableId)
        .then(() => this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST }));
    },
    restoreGroups(payload) {
      this.loading = true;
      this.groupList = [];
      ParametersTabService.restoreGroups(payload)
        .then(() => {
          this.$pushAlert({ code: 'MS016', mode: alertModes.TOAST });
        }).finally(() => {
          this.loading = false;
          this.getGroupList();
        });
    },
    searchChange(search) {
      this.variables = this.variableList
        .filter(({ name }) => name.toLocaleLowerCase().startsWith(search.toLocaleLowerCase()));
    },
  },
};
</script>
